import { AppPermissionResource } from "./AppPermissions";
import {
    AppPermissionIdentifierWithoutWildcard,
    AppPermissionIdentifierWithEffect,
    AppPermissionResourceWithEffect,
    PermissionEffect,
} from "./types";
import type { AppPermissionIdentifierWithEffectWithoutWildcard } from "./internal";

/**
 * Checks if a permission set is authorized against a specific permission.
 * @param requiredPermission The permission to check for
 * @param permissionRules Permissions to check against
 * @param mode If "strict", will return false if the permission is not found. If "loose", will return undefined if the permission is not found. Defaults to `"strict"`.
 * @example
 * ```ts
 * const permissions = [
 *   "Admin::IOTDevice::allow",
 *   "User::*::deny",
 * ];
 *
 * hasAppPermission("Admin::IOTDevice", permissions); // true
 * hasAppPermission("User::Profile", permissions); // false
 * hasAppPermission("Guest::Profile", permissions); // false (strict mode)
 * hasAppPermission("Guest::Profile", permissions, "loose"); // undefined (loose mode)
 * ```
 */
export function hasAppPermission<T extends "strict" | "loose" = "strict">(
    requiredPermission: AppPermissionIdentifierWithoutWildcard,
    permissionRules?: unknown,
    mode: T = "strict" as T
): T extends "strict" ? boolean : boolean | undefined {
    const [namespace, scope] = requiredPermission.split("::") as [
        keyof AppPermissionResource,
        AppPermissionResource[keyof AppPermissionResource][number]
    ];

    const permissions = convertWildcardToIdentifiers(
        normalizeAppPermissions(permissionRules)
    )
        // Filter out permissions that don't match namespace or scope
        .filter((p) => {
            const [userNamespace, userScope] = p.split("::");
            return userNamespace === namespace && userScope === scope;
        })
        .sort(sortPermissions); // the first permission should be the one that applies

    const permissionToCheck = permissions[0];

    if (!permissionToCheck)
        return (mode === "strict" ? false : undefined) as T extends "strict"
            ? boolean
            : boolean | undefined;

    const [userNamespace, userScope, userPermission] =
        permissionToCheck.split("::");

    const namespaceIsSame = userNamespace === namespace;

    // Check exact match
    if (namespaceIsSame && userScope === scope)
        return userPermission === "allow";

    return mode === "strict"
        ? false
        : (undefined as T extends "strict" ? boolean : boolean | undefined);
}

export function normalizeAppPermissions(
    permissions: unknown
): AppPermissionIdentifierWithEffect[] {
    if (Array.isArray(permissions)) {
        return permissions.filter(
            (permission): permission is AppPermissionIdentifierWithEffect =>
                typeof permission === "string" &&
                validateAppPermission(permission)
        );
    }

    if (
        permissions &&
        typeof permissions === "object" &&
        "permissions" in permissions
    ) {
        return normalizeAppPermissions(permissions.permissions);
    }

    if (permissions && typeof permissions === "object") {
        return serializeAppPermissions(
            permissions as AppPermissionResourceWithEffect
        ).filter(validateAppPermission);
    }

    return [];
}

function generateDefaultPermissionResourceWithEffect() {
    return Object.keys(AppPermissionResource).reduce((acc, cur) => {
        // @ts-expect-error keys
        acc[cur] = AppPermissionResource[cur].reduce((acc2, cur2) => {
            acc2[cur2] = null;
            return acc2;
        }, {} as any);
        return acc;
    }, {} as AppPermissionResourceWithEffect);
}

function convertWildcardToIdentifiers(
    permissions: AppPermissionIdentifierWithEffect[]
): AppPermissionIdentifierWithEffectWithoutWildcard[] {
    const result: string[] = [];

    permissions.forEach((permission) => {
        if (!validateAppPermission(permission.trim())) return;

        const [namespace, scope, effect] = permission.trim().split("::");

        if (namespace === "*") {
            Object.keys(AppPermissionResource).forEach((key) => {
                const typedKey = key as keyof AppPermissionResource;
                AppPermissionResource[typedKey]
                    .filter((permissionName) => {
                        if (scope === "*") return true;
                        if (scope.includes("*"))
                            return permissionName.startsWith(
                                scope.replace("*", "")
                            );
                        return permissionName === scope;
                    })
                    .forEach((permissionName) => {
                        result.push(
                            `${typedKey}::${permissionName}::${effect}`
                        );
                    });
            });
            return;
        }
        if (scope.includes("*")) {
            AppPermissionResource[namespace as keyof AppPermissionResource]
                .filter((permissionName) => {
                    if (scope === "*") return true;
                    return permissionName.startsWith(scope.replace("*", ""));
                })
                .forEach((permissionName) => {
                    const typedKey =
                        permissionName as keyof AppPermissionResource;
                    result.push(`${namespace}::${typedKey}::${effect}`);
                });
            return;
        }

        result.push(permission);
    });

    return result as AppPermissionIdentifierWithEffectWithoutWildcard[];
}

function sortPermissions(
    x: AppPermissionIdentifierWithEffectWithoutWildcard,
    y: AppPermissionIdentifierWithEffectWithoutWildcard
): number {
    const [xNamespace, xScope, xPermission] = x.split("::");
    const [yNamespace, yScope, yPermission] = y.split("::");

    // All deny permissions should be sorted first
    if (xPermission === "deny" && yPermission === "allow") {
        return -1;
    }
    if (xPermission === "allow" && yPermission === "deny") {
        return 1;
    }

    // Sort by namespace, then scope
    if (xNamespace === yNamespace) {
        return xScope.localeCompare(yScope);
    }

    return xNamespace.localeCompare(yNamespace);
}

export function validateAppPermission(value: string): boolean {
    const parts = value.split("::");
    if (parts.length !== 3) return false;
    const [namespace, scope, effect] = parts;
    if (!namespace || !scope || !effect) {
        // not a valid permission
        return false;
    }
    const namespaces = Object.keys(AppPermissionResource);
    if (!(namespaces.includes(namespace) || namespace === "*")) {
        // not a valid namespace
        return false;
    }
    const scopes =
        AppPermissionResource[namespace as keyof AppPermissionResource];
    if (!(scopes?.includes(scope as never) || scope.includes("*"))) {
        // not a valid scope
        return false;
    }
    if (!(effect === "allow" || effect === "deny")) {
        // not a valid effect
        return false;
    }
    return true;
}

export function deserializeAppPermissions(
    permissions: unknown
): AppPermissionResourceWithEffect {
    const defaultPermissions = generateDefaultPermissionResourceWithEffect();
    convertWildcardToIdentifiers(normalizeAppPermissions(permissions))
        .sort(sortPermissions)
        .forEach((permission) => {
            if (!validateAppPermission(permission)) return;
            const [namespace, scope, effect] = permission.split("::");
            const toCheck =
                // @ts-expect-error keys
                defaultPermissions[namespace as keyof AppPermissionResource][
                    scope as any
                ];
            if (toCheck === undefined || toCheck === null) {
                // @ts-expect-error keys
                defaultPermissions[namespace as keyof AppPermissionResource][
                    scope as any
                ] = effect as PermissionEffect;
            }
        });
    return defaultPermissions;
}

export function serializeAppPermissions(
    permissions: AppPermissionResourceWithEffect
): AppPermissionIdentifierWithEffect[] {
    const result: AppPermissionIdentifierWithEffect[] = [];
    Object.entries(permissions).forEach(([namespace, scopes]) => {
        Object.entries(scopes).forEach(([scope, effect]) => {
            if (effect) {
                result.push(`${namespace}::${scope}::${effect}` as any);
            }
        });
    });
    return result;
}
